'use client'

import Link from 'next/link'
import {
  ComponentProps,
  createContext,
  forwardRef,
  type ReactNode,
  use,
} from 'react'

export type AppLinkProps = Omit<ComponentProps<typeof Link>, 'href'> & {
  href: '/' | `/${string}`
}

/**
 * Parts of tbf.digital can be made available under a custom domain.
 * When the app is reached through that custom domain, the middleware
 * rewrites the request to prefix the configured base path.
 *
 * This component renders a next/link but prepends the base path (`/projects/xy/app`)
 * when the app is _not_ running on a custom domain.
 */
export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(
  function AppLink({ children, href, ...props }, ref) {
    const appPath = use(AppPathContext)

    return (
      <Link
        ref={ref}
        href={
          appPath && href === '/'
            ? appPath
            : href.startsWith(appPath)
              ? href
              : appPath + href
        }
        {...props}
      >
        {children}
      </Link>
    )
  },
)

export const AppPathContext = createContext<string>('')

export function AppPathProvider({
  children,
  path,
}: {
  children: ReactNode
  path: string
}) {
  return <AppPathContext value={path}>{children}</AppPathContext>
}
